import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Grid, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { formatCurrency, formatDate } from '../../../../utils/formatters';
import { calcPrecioFinal, calcPrecioNetoDesdeCostoYMkp } from '../../../../utils/calcPrecioFinal';
import { LABEL_TIPO_MONEDA_PESOS } from '../../../../utils/getTipoMonedaDescripcion';
import type { ProductoInfo360Maestro } from './ArticuloInfo360Header';
import type { ArticuloInfo360Indicadores, ArticuloInfo360Movimiento } from '../types';
import { formatCantidad, resolveUnidadMedida } from '../formatCantidad';
import {
  resolveNombreClase,
  resolveNombreLinea,
  resolveNombreRamo,
} from '../productoClasificacion';
import { fetchPreciosMinMaxLista } from '../resolvePreciosListaArticulo';
import { resolveStockOperativo } from '../resolveStockOperativo';

interface Props {
  producto: ProductoInfo360Maestro;
  data: ArticuloInfo360Indicadores | null;
  loadingIndicadores?: boolean;
}

const MetricLine: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={600} textAlign="right">{value}</Typography>
  </Stack>
);

const MovimientoRow: React.FC<{ mov: ArticuloInfo360Movimiento; unidadMedida?: string }> = ({
  mov,
  unidadMedida,
}) => (
  <Stack direction="row" justifyContent="space-between" spacing={1}>
    <Box minWidth={0}>
      <Typography variant="body2" fontWeight={600} noWrap>
        {mov.descripcion}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {mov.tipo} · {formatDate(mov.fecha)}
        {mov.deposito ? ` · Dep. ${mov.deposito}` : ''}
      </Typography>
    </Box>
    <Typography variant="body2" fontWeight={600} whiteSpace="nowrap">
      {formatCantidad(mov.cantidad, unidadMedida)}
    </Typography>
  </Stack>
);

function labelMoneda(tipoMoneda: number | null | undefined): string {
  if (tipoMoneda == null) return '—';
  if (Number(tipoMoneda) === 0) return LABEL_TIPO_MONEDA_PESOS;
  return String(tipoMoneda);
}

const ArticuloInfo360TabResumen: React.FC<Props> = ({
  producto,
  data,
  loadingIndicadores = false,
}) => {
  const um = resolveUnidadMedida(data?.unidadMedida, producto.idUnidadMedida);

  const [preciosLista, setPreciosLista] = useState<{
    minNeto: number | null;
    maxNeto: number | null;
    loading: boolean;
  }>({ minNeto: null, maxNeto: null, loading: true });

  useEffect(() => {
    let cancelled = false;
    setPreciosLista((prev) => ({ ...prev, loading: true }));

    void fetchPreciosMinMaxLista(producto).then((result) => {
      if (cancelled) return;
      setPreciosLista({
        minNeto: result.minNeto,
        maxNeto: result.maxNeto,
        loading: false,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [producto.codigoArticulo]);

  const precios = useMemo(() => {
    const costo = producto.costo ?? data?.costo ?? null;
    const precioNetoActual = producto.precioVenta ?? data?.precioVenta ?? null;
    const precioFinalActual = producto.precio ?? (precioNetoActual != null
      ? calcPrecioFinal({
        precioVenta: precioNetoActual,
        calculaIva: producto.calculaIva,
        ivaCF: producto.ivaCF,
      })
      : null);

    const ivaInput = {
      calculaIva: producto.calculaIva,
      ivaCF: producto.ivaCF,
    };

    const precioMinNetoLista = preciosLista.minNeto;
    const precioMinNetoEstimado = calcPrecioNetoDesdeCostoYMkp(costo, producto.porcentajeMkpminimo);
    const precioMinNeto = precioMinNetoLista ?? precioMinNetoEstimado;
    const precioMinEsEstimacion = precioMinNetoLista == null && precioMinNetoEstimado != null;
    const precioMinFinal = precioMinNeto != null
      ? calcPrecioFinal({ precioVenta: precioMinNeto, ...ivaInput })
      : null;

    const precioMaxNeto = preciosLista.maxNeto;
    const precioMaxFinal = precioMaxNeto != null
      ? calcPrecioFinal({ precioVenta: precioMaxNeto, ...ivaInput })
      : null;

    const costoLabel = costo != null
      ? `${formatCurrency(costo)}${producto.fechaUltCosto ? ` (${formatDate(producto.fechaUltCosto)})` : ''}`
      : '—';

    return {
      costoLabel,
      precioNetoActual,
      precioFinalActual,
      precioMinNeto,
      precioMinFinal,
      precioMinEsEstimacion,
      precioMaxNeto,
      precioMaxFinal,
      preciosListaLoading: preciosLista.loading,
    };
  }, [producto, data, preciosLista]);

  const atributos = [producto.atributo1, producto.atributo2, producto.atributo3]
    .map((a) => a?.trim())
    .filter(Boolean);

  const stockMin = producto.stockMin ?? data?.stockMin;
  const stockMax = producto.stockMax ?? data?.stockMax;
  const stockOptimo = producto.stockOptimo;

  const stockGlobal = resolveStockOperativo(producto, data);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Datos del artículo
          </Typography>
          <Stack spacing={1}>
            <MetricLine label="Código" value={producto.codigoArticulo} />
            <MetricLine label="Tipo" value={producto.tipoArticulo || '—'} />
            <MetricLine label="Ramo" value={resolveNombreRamo(producto) || '—'} />
            <MetricLine label="Línea" value={resolveNombreLinea(producto) || '—'} />
            <MetricLine label="Clase" value={resolveNombreClase(producto) || '—'} />
            <MetricLine
              label="Proveedor"
              value={producto.codigoProveedor || (producto.codProveedor != null ? `#${producto.codProveedor}` : '—')}
            />
            <MetricLine label="UM" value={um || '—'} />
            {atributos.length > 0 ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Atributos
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {atributos.map((attr) => (
                    <Chip key={attr} size="small" label={attr} variant="outlined" />
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Datos comerciales
          </Typography>
          <Stack spacing={1}>
            <MetricLine label="Moneda" value={labelMoneda(producto.tipoMoneda)} />
            <MetricLine label="Lista de precios" value={producto.lista?.trim() || '—'} />
            <MetricLine label="Costo" value={precios.costoLabel} />
            <MetricLine
              label="Precio neto (actual)"
              value={precios.precioNetoActual != null ? formatCurrency(precios.precioNetoActual) : '—'}
            />
            <MetricLine
              label="Precio final (actual)"
              value={precios.precioFinalActual != null ? formatCurrency(precios.precioFinalActual) : '—'}
            />
            {precios.preciosListaLoading && precios.precioMinNeto == null ? (
              <>
                <Skeleton height={24} />
                <Skeleton height={24} />
              </>
            ) : null}
            {precios.precioMinNeto != null ? (
              <>
                <MetricLine
                  label={precios.precioMinEsEstimacion ? 'Precio mín. neto (est.)' : 'Precio mín. neto'}
                  value={formatCurrency(precios.precioMinNeto)}
                />
                <MetricLine
                  label={precios.precioMinEsEstimacion ? 'Precio mín. final (est.)' : 'Precio mín. final'}
                  value={precios.precioMinFinal != null ? formatCurrency(precios.precioMinFinal) : '—'}
                />
              </>
            ) : null}
            {precios.precioMaxNeto != null && precios.precioMaxNeto !== precios.precioMinNeto ? (
              <>
                <MetricLine label="Precio máx. neto" value={formatCurrency(precios.precioMaxNeto)} />
                <MetricLine
                  label="Precio máx. final"
                  value={precios.precioMaxFinal != null ? formatCurrency(precios.precioMaxFinal) : '—'}
                />
              </>
            ) : null}
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Stock operativo global
          </Typography>
          <Stack spacing={1}>
            <MetricLine label="En producción" value={formatCantidad(stockGlobal.produccion, um)} />
            <MetricLine label="OC pendiente" value={formatCantidad(stockGlobal.oc, um)} />
            <MetricLine label="Reservado" value={formatCantidad(stockGlobal.reservado, um)} />
            <MetricLine label="Disp. venta" value={formatCantidad(stockGlobal.dispVenta, um)} />
            <MetricLine label="Cant. total" value={formatCantidad(stockGlobal.total, um)} />
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Umbrales y demanda
          </Typography>
          {loadingIndicadores && !data ? (
            <Stack spacing={1}>
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={24} />)}
            </Stack>
          ) : (
            <Stack spacing={1}>
              <MetricLine label="Stock mínimo" value={formatCantidad(stockMin, um)} />
              <MetricLine label="Stock óptimo" value={formatCantidad(stockOptimo, um)} />
              <MetricLine label="Stock máximo" value={formatCantidad(stockMax, um)} />
              <MetricLine label="Demanda mensual (6m)" value={formatCantidad(data?.demandaMensual6m, um)} />
              <MetricLine
                label="Tendencia ventas"
                value={data?.tendenciaVentasPct == null
                  ? 'Sin período comparable'
                  : `${data.tendenciaVentasPct > 0 ? '+' : ''}${data.tendenciaVentasPct}%`}
              />
            </Stack>
          )}
        </Paper>
      </Grid>

      {data && data.movimientosRecientes.length > 0 ? (
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Movimientos recientes (top 5)
            </Typography>
            <Stack spacing={1.25}>
              {data.movimientosRecientes.map((mov) => (
                <MovimientoRow
                  key={`${mov.fecha}-${mov.descripcion}-${mov.cantidad}`}
                  mov={mov}
                  unidadMedida={um}
                />
              ))}
            </Stack>
          </Paper>
        </Grid>
      ) : loadingIndicadores ? (
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Skeleton height={120} />
          </Paper>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default ArticuloInfo360TabResumen;
